import type { LessonProgress, Material, MaterialLinkType } from '@facecontrol/shared';
import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Gallery } from '../components/Gallery';
import { IconArrow, IconAudio, IconBack, IconBroadcast, IconCheck, IconChevron, IconExternal, IconPhoto, IconPlay, IconPost, IconSummary } from '../components/icons';
import { kindLabel, Photo } from '../components/MaterialCard';
import { Player } from '../components/Player';
import { dayMonth, imageSrc, monthKey, monthLong, plural } from '../lib/format';
import { openExternal } from '../lib/tg';

const LINK_LABEL: Record<MaterialLinkType, string> = { post: 'Пост урока в канале', summary: 'Конспект', broadcast: 'Эфир', audio: 'Аудио' };
const LINK_ICON: Record<MaterialLinkType, () => ReactElement> = { post: IconPost, summary: IconSummary, broadcast: IconBroadcast, audio: IconAudio };

interface Props {
  material: Material;
  entering: boolean;
  progress?: LessonProgress;
  /** the lesson's month in publishing order — for «Урок N», previous and next */
  siblings: Material[];
  track: (kind: 'open' | 'progress' | 'complete', position?: number) => void;
  onLessons: () => void;
  onOpenOther: (m: Material) => void;
}

/** A member may mark a lesson done herself once she has spent real time on it (players don't always report progress). */
const MANUAL_AFTER_MS = { video: 3 * 60_000, photo: 20_000 } as const;

export function MaterialScreen({ material, entering, progress, siblings, track, onLessons, onOpenOther }: Props) {
  const [rising, setRising] = useState(entering);
  const sentComplete = useRef(!!progress?.completedAt);
  const [doneAt, setDoneAt] = useState<string | null>(progress?.completedAt ?? null);
  const [canMark, setCanMark] = useState(false);
  const [part, setPart] = useState(0);
  const [pos, setPos] = useState(progress?.position ?? 0);
  useEffect(() => {
    track('open');
    setPart(0);
    const t = setTimeout(() => setRising(false), 700);
    return () => clearTimeout(t);
  }, [material.id, track]);
  useEffect(() => {
    setCanMark(false);
    const t = setTimeout(() => setCanMark(true), MANUAL_AFTER_MS[material.contentType]);
    return () => clearTimeout(t);
  }, [material.id, material.contentType]);

  const onProgress = useCallback(
    (p: number) => {
      setPos((x) => Math.max(x, p));
      track('progress', p);
    },
    [track],
  );
  const onComplete = useCallback(() => {
    if (sentComplete.current) return;
    sentComplete.current = true;
    setDoneAt(new Date().toISOString());
    setPos(1);
    track('complete', 1);
  }, [track]);

  const i = siblings.findIndex((m) => m.id === material.id);
  const prev = i > 0 ? siblings[i - 1] : null;
  const next = i >= 0 && i < siblings.length - 1 ? siblings[i + 1] : null;
  const pct = doneAt ? 100 : Math.round(pos * 100);
  const isVideo = material.contentType === 'video';
  const tabs = [material.description ? ('about' as const) : null, material.links.length ? ('links' as const) : null].filter((t): t is 'about' | 'links' => !!t);
  const [tab, setTab] = useState<'about' | 'links'>(tabs[0] ?? 'about');
  const activeTab = tabs.includes(tab) ? tab : tabs[0];

  return (
    <article className={`page lesson${rising ? ' is-rising' : ''}`}>
      <nav className="crumbs" aria-label="Путь">
        <button type="button" onClick={onLessons}>
          Уроки
        </button>
        <span aria-hidden="true">/</span>
        <span>{monthLong(monthKey(new Date(material.createdAt)))}</span>
      </nav>
      <header className="lesson__head">
        <h1 className="h-display lesson__title">{material.name}</h1>
        {material.subtitle && <p className="lesson__sub">{material.subtitle}</p>}
      </header>

      <div className="lesson__main">
        <div className="lesson__media">{isVideo ? <Player parts={material.video} idx={part} onProgress={onProgress} onComplete={onComplete} /> : <Gallery photos={material.photos} onLast={onComplete} />}</div>

        <aside className="lesson__side" aria-label="Об уроке">
          <h2 className="h-section">В этом уроке</h2>
          <ol className="parts">
            {isVideo && material.video.length > 1 ? (
              material.video.map((_, n) => (
                <li key={n}>
                  <button type="button" className={n === part ? 'is-on' : ''} aria-current={n === part ? 'true' : undefined} onClick={() => setPart(n)}>
                    <i>{n + 1}</i>
                    <span>Часть {n + 1}</span>
                    <IconChevron />
                  </button>
                </li>
              ))
            ) : (
              <>
                <li>
                  <span className="parts__row">
                    <i>{i >= 0 ? i + 1 : '1'}</i>
                    <span>{i >= 0 ? `Урок ${i + 1} · ${kindLabel(material)}` : kindLabel(material)}</span>
                  </span>
                </li>
                <li>
                  <span className="parts__row">
                    <i aria-hidden="true">{isVideo ? <IconPlay /> : <IconPhoto />}</i>
                    <span>{isVideo ? `Вышел ${dayMonth(material.createdAt)}` : `${plural(material.photos.length, 'фото', 'фото', 'фото')} · ${dayMonth(material.createdAt)}`}</span>
                  </span>
                </li>
                {material.links.length > 0 && (
                  <li>
                    <span className="parts__row">
                      <i aria-hidden="true">
                        <IconSummary />
                      </i>
                      <span>{plural(material.links.length, 'гайд', 'гайда', 'гайдов')} к уроку</span>
                    </span>
                  </li>
                )}
              </>
            )}
          </ol>
          <div className="lesson__progress">
            <span className="lesson__progress-top">
              <span className="h-small">Твой прогресс</span>
              <b>{pct}%</b>
            </span>
            <span className="bar bar--knob" aria-hidden="true">
              <i style={{ width: `${pct}%` }} />
            </span>
          </div>
          {doneAt ? (
            <p className="lesson__done">
              <IconCheck /> Урок пройден
            </p>
          ) : (
            <button type="button" className="btn btn--ghost" disabled={!canMark} onClick={onComplete}>
              {canMark ? 'Отметить пройденным' : isVideo ? 'Отметить можно после просмотра' : 'Пролистайте гайд до конца'}
            </button>
          )}
        </aside>
      </div>

      {tabs.length > 0 && (
        <section className="lesson__more">
          {tabs.length > 1 ? (
            <div className="tabs" role="tablist" aria-label="Об уроке">
              {tabs.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={activeTab === t} className={activeTab === t ? 'is-on' : ''} onClick={() => setTab(t)}>
                  {t === 'about' ? 'Об уроке' : 'Гайды'}
                </button>
              ))}
            </div>
          ) : (
            <div className="tabs tabs--single">
              <span className="is-on">{activeTab === 'about' ? 'Об уроке' : 'Гайды'}</span>
            </div>
          )}
          {activeTab === 'about' && <p className="lesson__desc">{material.description}</p>}
          {activeTab === 'links' && (
            <nav className="links" aria-label="Гайды к уроку">
              {material.links.map((l, n) => {
                const Icon = LINK_ICON[l.type] ?? IconPost;
                return (
                  <a
                    key={`${l.link}-${n}`}
                    className="link-row"
                    href={l.link}
                    onClick={(e) => {
                      e.preventDefault();
                      openExternal(l.link);
                    }}
                  >
                    <Icon />
                    <span>{l.title || LINK_LABEL[l.type] || 'Открыть'}</span>
                    <IconExternal />
                  </a>
                );
              })}
            </nav>
          )}
        </section>
      )}

      {(prev || next) && (
        <nav className="pager" aria-label="Соседние уроки">
          {prev ? (
            <button type="button" className="pager__item" onClick={() => onOpenOther(prev)}>
              <Photo src={imageSrc(prev.preview)} name={prev.name} className="pager__photo photo" />
              <span>
                <span className="pager__dir">
                  <IconBack /> Предыдущий урок
                </span>
                <span className="pager__name">{prev.name}</span>
              </span>
            </button>
          ) : (
            <span />
          )}
          {next ? (
            <button type="button" className="pager__item pager__item--next" onClick={() => onOpenOther(next)}>
              <span>
                <span className="pager__dir">
                  Следующий урок <IconArrow />
                </span>
                <span className="pager__name">{next.name}</span>
              </span>
              <Photo src={imageSrc(next.preview)} name={next.name} className="pager__photo photo" />
            </button>
          ) : (
            <span />
          )}
        </nav>
      )}
    </article>
  );
}
